import { useTranslation } from 'react-i18next';
import { SegmentedControl } from './SegmentedControl.js';

export type DateRangePreset =
  | 'today'
  | '7d'
  | '30d'
  | '90d'
  | 'this_month'
  | 'last_month'
  | 'this_year'
  | 'all'
  | 'custom';

export interface DateRangeValue {
  preset: DateRangePreset;
  /** ISO yyyy-mm-dd; only meaningful when preset === 'custom' */
  from?: string;
  to?: string;
}

const PRESET_KEYS: Record<DateRangePreset, string> = {
  today: 'common.dateRange.today',
  '7d': 'common.dateRange.days7',
  '30d': 'common.dateRange.days30',
  '90d': 'common.dateRange.days90',
  this_month: 'common.dateRange.thisMonth',
  last_month: 'common.dateRange.lastMonth',
  this_year: 'common.dateRange.thisYear',
  all: 'common.dateRange.allTime',
  custom: 'common.dateRange.custom',
};

/** The standard preset set. Every date-filtered page uses this unless it has a reason not to. */
export const DEFAULT_PRESETS: readonly DateRangePreset[] = [
  'today',
  '7d',
  '30d',
  'this_month',
  'last_month',
  'this_year',
  'custom',
];

function iso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysAgo(days: number): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - days);
  return d;
}

/** Resolve a DateRangeValue into concrete ISO day bounds (undefined = unbounded). */
export function resolveDateRange(v: DateRangeValue): { from?: string; to?: string } {
  const now = new Date();
  const today = iso(now);
  switch (v.preset) {
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: iso(daysAgo(6)), to: today };
    case '30d':
      return { from: iso(daysAgo(29)), to: today };
    case '90d':
      return { from: iso(daysAgo(89)), to: today };
    case 'this_month':
      return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    case 'last_month': {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return { from: iso(first), to: iso(last) };
    }
    case 'this_year':
      return { from: iso(new Date(now.getFullYear(), 0, 1)), to: today };
    case 'all':
      return {};
    case 'custom':
      return { from: v.from, to: v.to };
  }
}

/** Same as resolveDateRange but as Date objects: start of `from` day, end of `to` day. */
export function resolveDateRangeDates(v: DateRangeValue): [Date, Date] {
  const r = resolveDateRange(v);
  const start = r.from ? new Date(`${r.from}T00:00:00`) : new Date(0);
  const end = r.to ? new Date(`${r.to}T23:59:59.999`) : new Date();
  return [start, end];
}

interface DateRangeProps {
  value: DateRangeValue;
  onChange: (v: DateRangeValue) => void;
  /** Defaults to DEFAULT_PRESETS. Only narrow this when the page truly cannot support a preset. */
  presets?: readonly DateRangePreset[];
  size?: 'sm' | 'md';
  /** Always show from/to under the presets. Books only — other pages stay one row. */
  alwaysDates?: boolean;
}

/**
 * The one date-range control. Presets inline; from/to appear next to them
 * when Custom is selected. `alwaysDates` pins from/to on a second row.
 */
export function DateRange({
  value,
  onChange,
  presets = DEFAULT_PRESETS,
  size = 'md',
  alwaysDates = false,
}: DateRangeProps) {
  const { t } = useTranslation();
  const options = presets.map((p) => ({ value: p, label: t(PRESET_KEYS[p]) }));
  const bounds = resolveDateRange(value);
  const from = bounds.from ?? '';
  const to = bounds.to ?? '';
  const showDates = alwaysDates || value.preset === 'custom';
  return (
    <div className={alwaysDates ? 'ui-daterange ui-daterange--stack' : 'ui-daterange'}>
      <SegmentedControl
        size={size}
        value={value.preset}
        options={options}
        ariaLabel={t('common.dateRange.ariaLabel')}
        onChange={(preset) => {
          if (preset === 'custom') {
            const r = resolveDateRange(value);
            onChange({ preset, from: r.from ?? iso(daysAgo(29)), to: r.to ?? iso(new Date()) });
          } else {
            onChange({ preset });
          }
        }}
      />
      {showDates && (
        <div className="ui-daterange-custom">
          <input
            type="date"
            aria-label={t('common.dateRange.from')}
            value={from}
            max={to || undefined}
            onChange={(e) => onChange({ preset: 'custom', from: e.target.value, to: to || e.target.value })}
          />
          <span className="ui-daterange-sep">→</span>
          <input
            type="date"
            aria-label={t('common.dateRange.to')}
            value={to}
            min={from || undefined}
            onChange={(e) => onChange({ preset: 'custom', from: from || e.target.value, to: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
